/**
 * NextStepGuide — auto-progression handoff po ukończonym kroku w START MODE.
 *
 * Filozofia (FINALNY KIERUNEK):
 *   - Po SAVE/PUBLISH każdego kroku pokazujemy "step complete" success state
 *   - + jasne CTA prowadzące do następnego kroku (nie do dashboardu!)
 *   - User płynie przez flow, nie wraca do menu
 *   - APISAI prowadzi jak premium product, nie panel administracyjny
 *
 * Użycie:
 *   <NextStepGuide
 *     open={publishedJustNow}
 *     currentStep={1}                        // 1..4
 *     userName={user?.name}
 *     creationType="kennel"                  // "kennel" | "litter" | "stud" | "business"
 *     onClose={() => setPublishedJustNow(false)}
 *   />
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, ArrowRight, X, ExternalLink, PartyPopper } from 'lucide-react';

/** Mapowanie creationType → docelowy wizard + copy. */
const TYPE_META = {
  kennel:   { wizard: '/wizard/hodowla-v2',  label: 'hodowli',     thing: 'hodowlę',     content: 'Pokaż swoje psy i mioty' },
  litter:   { wizard: '/wizard/miot',        label: 'miocie',       thing: 'miot',         content: 'Dodaj zdjęcia szczeniąt' },
  stud:     { wizard: '/wizard/reproduktor', label: 'reproduktorze', thing: 'reproduktora', content: 'Dodaj zdjęcia i osiągnięcia' },
  business: { wizard: '/wizard/firma-v2',    label: 'firmie',       thing: 'firmę',        content: 'Dodaj usługi, portfolio i zdjęcia z realizacji.' },
};

/** Konfiguracja każdego z 4 kroków START MODE — w nowej kolejności. */
function getNextStepConfig(currentStep, creationType) {
  const meta = TYPE_META[creationType] || TYPE_META.kennel;
  const map = {
    1: {
      doneTitle: 'Wygląd strony zapisany',
      doneSub: 'Strona nie jest jeszcze publiczna — dokończ profil żeby trafiła do katalogu APISAI.',
      nextEyebrow: 'Krok 2 z 4',
      nextTitle: `Uzupełnij informacje o ${meta.label}`,
      nextSub: 'Nazwa, opis, lokalizacja, kontakt — spokojnie, sekcjami, z preview obok.',
      cta: 'Przejdź dalej',
      ctaTo: meta.wizard,
    },
    2: {
      doneTitle: 'Informacje uzupełnione',
      doneSub: 'Profil ma już komplet podstawowych danych.',
      nextEyebrow: 'Krok 3 z 4',
      nextTitle: meta.content,
      nextSub: creationType === 'business'
        ? 'Dodaj usługi, portfolio i zdjęcia z realizacji.'
        : 'Pokaż klientom to, co najważniejsze.',
      cta: 'Przejdź dalej',
      ctaTo: meta.wizard,
    },
    3: {
      doneTitle: 'Oferta gotowa',
      doneSub: 'Klienci zobaczą czym się zajmujesz.',
      nextEyebrow: 'Krok 4 z 4',
      nextTitle: 'Opublikuj swoją stronę',
      nextSub: 'Strona trafi do katalogu APISAI i będzie widoczna online.',
      cta: 'Opublikuj stronę',
      ctaTo: meta.wizard,
    },
    4: {
      doneTitle: 'Strona opublikowana',
      doneSub: 'Jesteś online — Twoja strona widoczna w katalogu APISAI.',
      nextEyebrow: 'Co dalej',
      nextTitle: 'Twoje profile i oferty',
      nextSub: 'Wróć tu kiedy chcesz — uzupełnij, opublikuj kolejną rzecz albo udostępnij link.',
      cta: 'Zobacz moje profile',
      ctaTo: '/panel/moje',
    },
  };
  return map[currentStep] || map[1];
}

export default function NextStepGuide({ open, currentStep = 1, userName, creationType, entityType, viewLink, onClose }) {
  const navigate = useNavigate();
  const [closing, setClosing] = useState(false);
  // Fallback: jeśli creationType nie podane, czytamy z localStorage albo z entityType (legacy prop).
  let resolvedType = creationType;
  if (!resolvedType && typeof window !== 'undefined') {
    try { resolvedType = window.localStorage.getItem('apisai_creation_type') || null; } catch (_e) { /* ignore */ }
  }
  if (!resolvedType) resolvedType = entityType === 'business' ? 'business' : 'kennel';
  const cfg = getNextStepConfig(currentStep, resolvedType);
  const greeting = userName?.split(' ')[0] || 'Świetnie';
  const isCelebration = currentStep === 4; // Publikacja zakończona — confetti + live link

  useEffect(() => {
    if (open) setClosing(false);
  }, [open]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose?.();
    }, 260);
  };

  const handleNext = () => {
    setClosing(true);
    setTimeout(() => {
      onClose?.();
      navigate(cfg.ctaTo);
    }, 240);
  };

  if (!open) return null;

  return (
    <>
      {/* Soft backdrop — nie blokujący, subtelnie ściemniający */}
      <div
        className={`fixed inset-0 z-50 bg-black/55 backdrop-blur-[3px] transition-opacity duration-300 ${closing ? 'opacity-0' : 'opacity-100'}`}
        onClick={handleClose}
        aria-hidden="true"
        data-testid="next-step-backdrop"
      />

      {/* Centered card */}
      <div
        className={[
          'fixed z-[60] inset-0 flex items-center justify-center p-4 pointer-events-none',
          'transition-all duration-300',
          closing ? 'opacity-0 scale-[0.98]' : 'opacity-100 scale-100',
        ].join(' ')}
        role="dialog"
        aria-modal="true"
        data-testid="next-step-guide"
      >
        <div
          className="relative pointer-events-auto w-full max-w-md rounded-3xl overflow-hidden ring-1 ring-white/8 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.85)]"
          style={{ background: 'linear-gradient(160deg, #04202F 0%, #052B3D 60%, #073447 100%)' }}
        >
          {/* Orange glow top-right */}
          <div
            className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-45"
            style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.55), transparent 65%)' }}
            aria-hidden="true"
          />

          <button
            onClick={handleClose}
            data-testid="next-step-dismiss"
            className="absolute top-4 right-4 w-8 h-8 rounded-lg flex items-center justify-center text-white/45 hover:text-white hover:bg-white/8 transition-colors"
            aria-label="Zamknij"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="relative p-7 sm:p-9 space-y-7">
            {/* SUCCESS — krok ukończony */}
            <div className="space-y-3" data-testid="next-step-done">
              <div className={[
                'inline-flex items-center gap-2 px-3 py-1 rounded-full ring-1',
                isCelebration ? 'bg-[var(--apisai-primary)]/15 ring-[var(--apisai-primary)]/40' : 'bg-emerald-400/15 ring-emerald-400/30',
              ].join(' ')}>
                {isCelebration ? (
                  <PartyPopper className="w-3.5 h-3.5 text-[var(--apisai-primary)]" strokeWidth={2.5} />
                ) : (
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" strokeWidth={2.5} />
                )}
                <span className={[
                  'text-[10px] font-bold uppercase tracking-[0.18em]',
                  isCelebration ? 'text-[var(--apisai-primary)]' : 'text-emerald-200',
                ].join(' ')}>
                  {isCelebration ? 'Twoja strona jest online' : `Krok ${currentStep} z 4 ukończony`}
                </span>
              </div>
              <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight leading-tight">
                {isCelebration ? `Gotowe, ${greeting}!` : `Świetnie, ${greeting}.`}
              </h2>
              <p className="text-white/70 text-sm sm:text-base leading-relaxed">
                {cfg.doneTitle}. {cfg.doneSub}
              </p>
            </div>

            {/* LIVE LINK — pokazuje się tylko gdy podano viewLink (np. po publikacji) */}
            {viewLink?.href && (
              <a
                href={viewLink.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="next-step-view-link"
                className="group flex items-center justify-between gap-4 p-4 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] ring-1 ring-white/10 hover:ring-[var(--apisai-primary)]/50 transition-all"
              >
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--apisai-primary)] mb-1">
                    Link do Twojej strony
                  </p>
                  <p className="text-white text-sm font-medium truncate group-hover:text-[var(--apisai-primary)] transition-colors">
                    {viewLink.label || viewLink.href}
                  </p>
                </div>
                <ExternalLink className="w-4 h-4 text-white/55 group-hover:text-[var(--apisai-primary)] flex-shrink-0 transition-colors" />
              </a>
            )}

            {/* DIVIDER */}
            <div className="h-px bg-white/8" />

            {/* NEXT — co dalej */}
            <div className="space-y-3" data-testid="next-step-next">
              <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[var(--apisai-primary)]">
                {cfg.nextEyebrow}
              </p>
              <h3 className="text-white text-xl font-semibold tracking-tight leading-tight">
                {cfg.nextTitle}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">
                {cfg.nextSub}
              </p>
            </div>

            {/* CTA */}
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={handleNext}
                data-testid="next-step-cta"
                className="group flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[var(--apisai-primary)] hover:bg-[var(--apisai-primary-600)] text-white text-sm font-semibold transition-all shadow-[0_12px_30px_-8px_rgba(249,115,22,0.6)] hover:-translate-y-0.5 min-h-[48px]"
              >
                {cfg.cta}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button
                onClick={handleClose}
                data-testid="next-step-stay"
                className="px-4 py-3 text-white/55 hover:text-white text-sm font-medium transition-colors min-h-[48px]"
              >
                Zostań tu
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
